import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  Grid,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Refresh as RefreshIcon,
  RestartAlt as ResetIcon,
  Settings as SettingsIcon,
  TextFields as TemplateIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import TemplateManager from './TemplateManager';

interface SiteUsage {
  _id: string;
  name: string;
  route: string;
  creator?: {
    username?: string;
    phoneNumber?: string;
  } | null;
  createdAt: string;
  usage: {
    dailyCount: number;
    monthlyCount: number;
    totalSent: number;
    dailyLimit: number;
    monthlyLimit: number;
    isEnabled: boolean;
    dailyRemaining: number;
    monthlyRemaining: number;
    lastResetDate: string;
    lastMonthlyResetDate: string;
  };
}

const GlobalAdminDashboard: React.FC = () => {
  const [sites, setSites] = useState<SiteUsage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSite, setEditingSite] = useState<SiteUsage | null>(null);
  const [editDailyLimit, setEditDailyLimit] = useState<number>(100);
  const [editMonthlyLimit, setEditMonthlyLimit] = useState<number>(1000);
  const [editIsEnabled, setEditIsEnabled] = useState<boolean>(true);
  const [saving, setSaving] = useState(false);
  
  // Reset dialog state
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetSite, setResetSite] = useState<SiteUsage | null>(null);
  const [resetType, setResetType] = useState<'daily' | 'monthly' | 'total'>('daily');
  
  // Template manager state
  const [showTemplateManager, setShowTemplateManager] = useState(false);

  useEffect(() => {
    fetchAllSitesUsage();
  }, []);

  const fetchAllSitesUsage = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/sites/whatsapp/all-sites-usage`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSites(response.data);
      setError(null);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('只有全域管理員可以訪問此頁面');
      } else {
        setError(err.response?.data?.message || '獲取群組使用情況失敗');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditLimits = (site: SiteUsage) => {
    setEditingSite(site);
    setEditDailyLimit(site.usage.dailyLimit);
    setEditMonthlyLimit(site.usage.monthlyLimit);
    setEditIsEnabled(site.usage.isEnabled);
    setEditDialogOpen(true);
  };

  const handleSaveLimits = async () => {
    if (!editingSite) return;
    
    try {
      setSaving(true);
      await axios.put(`${API_BASE_URL}/sites/whatsapp/limits`, {
        siteId: editingSite._id,
        dailyLimit: editDailyLimit,
        monthlyLimit: editMonthlyLimit,
        isEnabled: editIsEnabled
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSuccess(`已更新群組 ${editingSite.name} 的限制設定`);
      setEditDialogOpen(false);
      fetchAllSitesUsage();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || '更新限制失敗');
    } finally {
      setSaving(false);
    }
  };

  const handleResetUsage = async () => {
    if (!resetSite) return;
    
    try {
      await axios.post(`${API_BASE_URL}/sites/whatsapp/reset-site`, {
        siteId: resetSite._id,
        resetType
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSuccess(`已重置群組 ${resetSite.name} 的使用統計`);
      setResetDialogOpen(false);
      fetchAllSitesUsage();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || '重置失敗');
    }
  };

  const getProgressColor = (current: number, limit: number) => {
    const percentage = (current / limit) * 100;
    if (percentage >= 90) return 'error';
    if (percentage >= 70) return 'warning';
    return 'primary';
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            全域管理 - WhatsApp 使用管理
          </Typography>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  if (error && error.includes('全域管理員')) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">
              全域管理 - WhatsApp 使用管理
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="模板管理">
                <Button
                  variant="outlined"
                  startIcon={<TemplateIcon />}
                  onClick={() => setShowTemplateManager(true)}
                  size="small"
                >
                  模板管理
                </Button>
              </Tooltip>
              <Tooltip title="刷新數據">
                <IconButton onClick={fetchAllSitesUsage}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>群組名稱</TableCell>
                  <TableCell>擁有者</TableCell>
                  <TableCell>狀態</TableCell>
                  <TableCell>每日使用</TableCell>
                  <TableCell>每月使用</TableCell>
                  <TableCell>總發送</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sites.map((site) => (
                  <TableRow key={site._id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {site.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          /{site.route}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {site.creator?.username || '—'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {site.creator?.phoneNumber || '—'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={site.usage.isEnabled ? '已啟用' : '已停用'} 
                        color={site.usage.isEnabled ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ minWidth: 120 }}>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min((site.usage.dailyCount / site.usage.dailyLimit) * 100, 100)}
                          color={getProgressColor(site.usage.dailyCount, site.usage.dailyLimit)}
                          sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
                        />
                        <Typography variant="caption">
                          {site.usage.dailyCount}/{site.usage.dailyLimit}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ minWidth: 120 }}>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min((site.usage.monthlyCount / site.usage.monthlyLimit) * 100, 100)}
                          color={getProgressColor(site.usage.monthlyCount, site.usage.monthlyLimit)}
                          sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
                        />
                        <Typography variant="caption">
                          {site.usage.monthlyCount}/{site.usage.monthlyLimit}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {site.usage.totalSent}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="編輯限制">
                        <IconButton onClick={() => handleEditLimits(site)} size="small">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="重置統計">
                        <IconButton 
                          onClick={() => {
                            setResetSite(site);
                            setResetDialogOpen(true);
                          }} 
                          size="small"
                        >
                          <ResetIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Edit Limits Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          編輯 WhatsApp 限制 - {editingSite?.name}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="每日限制"
                type="number"
                value={editDailyLimit}
                onChange={(e) => setEditDailyLimit(parseInt(e.target.value) || 0)}
                fullWidth
                inputProps={{ min: 0, max: 10000 }}
                helperText="0-10000條訊息"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="每月限制"
                type="number"
                value={editMonthlyLimit}
                onChange={(e) => setEditMonthlyLimit(parseInt(e.target.value) || 0)}
                fullWidth
                inputProps={{ min: 0, max: 100000 }}
                helperText="0-100000條訊息"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editIsEnabled}
                    onChange={(e) => setEditIsEnabled(e.target.checked)}
                  />
                }
                label="啟用WhatsApp發送限制"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>取消</Button>
          <Button onClick={handleSaveLimits} variant="contained" disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Usage Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>重置使用統計 - {resetSite?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            選擇要重置的統計類型：
          </Typography>
          <Box>
            <Button
              variant={resetType === 'daily' ? 'contained' : 'outlined'}
              onClick={() => setResetType('daily')}
              sx={{ mr: 1, mb: 1 }}
            >
              每日
            </Button>
            <Button
              variant={resetType === 'monthly' ? 'contained' : 'outlined'}
              onClick={() => setResetType('monthly')}
              sx={{ mr: 1, mb: 1 }}
            >
              每月
            </Button>
            <Button
              variant={resetType === 'total' ? 'contained' : 'outlined'}
              onClick={() => setResetType('total')}
              sx={{ mb: 1 }}
            >
              全部
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>取消</Button>
          <Button onClick={handleResetUsage} color="error" variant="contained">
            確認重置
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Manager */}
      <TemplateManager
        open={showTemplateManager}
        onClose={() => setShowTemplateManager(false)}
      />
    </Box>
  );
};

export default GlobalAdminDashboard; 